import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileText, X, Loader2, User, Save, CheckCircle } from 'lucide-react';
import { cvAutofill, createCandidate, getCandidateDetail } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './ProfilePage.css';

const ProfilePage = () => {
  const { user, updateCandidateId } = useAuth();
  const [cvFile, setCvFile] = useState(null);
  const [cvLoading, setCvLoading] = useState(false);
  const [cvProfile, setCvProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name: user?.name || '',
    surname: user?.surname || '',
    email: user?.email || '',
    phone: '',
    title: '',
    birthdate: '',
    location: '',
    linkedinUrl: '',
    experienceYear: '',
    education: '',
    skills: '',
    summary: '',
    gender: '',
    nationality: '',
    militaryStatus: '',
  });

  // Mevcut profil varsa yükle
  useEffect(() => {
    if (!user?.candidateId) return;
    setLoadingProfile(true);
    getCandidateDetail(user.candidateId)
      .then((res) => {
        const c = res.candidate || res.data?.candidate || res.data || {};
        setForm(prev => ({
          ...prev,
          name: c.name || prev.name,
          surname: c.surname || prev.surname,
          email: c.email || prev.email,
          phone: c.phone || prev.phone,
          title: c.title || prev.title,
          birthdate: c.birthdate ? c.birthdate.split('T')[0] : prev.birthdate,
          location: c.location || prev.location,
          linkedinUrl: c.linkedinUrl || prev.linkedinUrl,
          experienceYear: c.experienceYear != null ? String(c.experienceYear) : prev.experienceYear,
          education: c.educationLevel || prev.education,
          skills: prev.skills,
          summary: c.summary || prev.summary,
          gender: c.gender || prev.gender,
          nationality: c.nationality || prev.nationality,
          militaryStatus: c.militaryStatus || prev.militaryStatus,
        }));
      })
      .catch(() => {})
      .finally(() => setLoadingProfile(false));
  }, [user?.candidateId]);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleCvUpload = async () => {
    if (!cvFile) return;
    setCvLoading(true);
    setError('');
    try {
      const data = await cvAutofill(cvFile);
      setCvProfile(data);
      setForm(prev => ({
        ...prev,
        name: data.name || prev.name,
        surname: data.surname || prev.surname,
        phone: data.phone || prev.phone,
        title: data.title || prev.title,
        location: data.currentLocation || prev.location,
        experienceYear: data.experienceYear != null ? String(data.experienceYear) : prev.experienceYear,
        education: data.education || prev.education,
        skills: Array.isArray(data.skills) ? data.skills.join(', ') : (data.skills || prev.skills),
        summary: data.summary || prev.summary,
        birthdate: data.birthdate ? data.birthdate.split('T')[0] : prev.birthdate,
        militaryStatus: data.militaryStatus || prev.militaryStatus,
      }));
    } catch {
      setError('CV analiz edilemedi. Lütfen tekrar deneyin.');
    } finally {
      setCvLoading(false);
    }
  };

  const removeCv = () => {
    setCvFile(null);
    setCvProfile(null);
    setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        surname: form.surname,
        email: form.email,
        phone: form.phone || null,
        title: form.title || null,
        birthdate: form.birthdate || null,
        location: form.location || null,
        linkedinUrl: form.linkedinUrl || null,
        summary: form.summary || null,
        militaryStatus: form.militaryStatus || null,
        educationLevel: form.education || null,
        experienceYear: form.experienceYear ? Number(form.experienceYear) : null,
        gender: form.gender || null,
        nationality: form.nationality || null,
      };
      const result = await createCandidate(payload);
      if (result.status && result.data) {
        updateCandidateId(result.data);
      }
      setSaved(true);
    } catch {
      setError('Profil kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="profile__container">
        <div className="profile__header">
          <h1 className="profile__title">Profilim</h1>
          <p className="profile__subtitle">
            {user?.candidateId
              ? 'Bilgilerini güncel tut, sana en uygun ilanları bulalım.'
              : 'Profilini oluştur, sana en uygun ilanları bulalım.'}
          </p>
        </div>

        {/* Profil Kartı */}
        <div className="profile__card">
          <div className="profile__card-top">
            <div className="profile__avatar">
              {user?.imageUrl ? (
                <img src={user.imageUrl} alt="" className="profile__avatar-img" />
              ) : (
                <User size={28} />
              )}
            </div>
            <div className="profile__card-info">
              <h2 className="profile__name">{user?.name} {user?.surname}</h2>
              <p className="profile__provider">{user?.provider} ile giriş yapıldı</p>
            </div>
          </div>
        </div>

        {loadingProfile ? (
          <div className="profile__section" style={{ textAlign: 'center', padding: '40px' }}>
            <Loader2 size={24} className="profile__spinner" style={{ color: '#2F3A8F' }} />
            <p style={{ color: '#6B7280', marginTop: '12px', fontSize: '14px' }}>Profil yükleniyor...</p>
          </div>
        ) : (
          <>
            {/* Kişisel Bilgiler */}
            <div className="profile__section">
              <h2 className="profile__section-title">Kişisel Bilgiler</h2>
              <div className="profile__form-grid">
                <div className="profile__field">
                  <label className="profile__label">Ad</label>
                  <input className="profile__input" value={form.name} onChange={(e) => updateField('name', e.target.value)} placeholder="Ad" />
                </div>
                <div className="profile__field">
                  <label className="profile__label">Soyad</label>
                  <input className="profile__input" value={form.surname} onChange={(e) => updateField('surname', e.target.value)} placeholder="Soyad" />
                </div>
                <div className="profile__field">
                  <label className="profile__label">E-posta</label>
                  <input className="profile__input" value={form.email} disabled placeholder="E-posta" />
                </div>
                <div className="profile__field">
                  <label className="profile__label">Telefon</label>
                  <input className="profile__input" value={form.phone} onChange={(e) => updateField('phone', e.target.value)} placeholder="05XX XXX XX XX" />
                </div>
                <div className="profile__field">
                  <label className="profile__label">Doğum Tarihi</label>
                  <input className="profile__input" type="date" value={form.birthdate} onChange={(e) => updateField('birthdate', e.target.value)} />
                </div>
                <div className="profile__field">
                  <label className="profile__label">Cinsiyet</label>
                  <select className="profile__input" value={form.gender} onChange={(e) => updateField('gender', e.target.value)}>
                    <option value="">Seçiniz</option>
                    <option value="Erkek">Erkek</option>
                    <option value="Kadın">Kadın</option>
                    <option value="Belirtmek istemiyorum">Belirtmek istemiyorum</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Kariyer Bilgileri */}
            <div className="profile__section">
              <h2 className="profile__section-title">Kariyer Bilgileri</h2>
              <p className="profile__section-hint">Bu bilgiler sana uygun ilanları bulmamızda kullanılır.</p>
              <div className="profile__form-grid">
                <div className="profile__field">
                  <label className="profile__label">Aradığın Pozisyon</label>
                  <input className="profile__input" value={form.title} onChange={(e) => updateField('title', e.target.value)} placeholder="Garson, Yazılımcı, Muhasebeci..." />
                </div>
                <div className="profile__field">
                  <label className="profile__label">Şehir / İl</label>
                  <input className="profile__input" value={form.location} onChange={(e) => updateField('location', e.target.value)} placeholder="İstanbul, Ankara..." />
                </div>
                <div className="profile__field">
                  <label className="profile__label">Deneyim (Yıl)</label>
                  <input className="profile__input" type="number" min="0" value={form.experienceYear} onChange={(e) => updateField('experienceYear', e.target.value)} placeholder="0" />
                </div>
                <div className="profile__field">
                  <label className="profile__label">Eğitim Durumu</label>
                  <select className="profile__input" value={form.education} onChange={(e) => updateField('education', e.target.value)}>
                    <option value="">Seçiniz</option>
                    <option value="Lise">Lise</option>
                    <option value="Ön Lisans">Ön Lisans</option>
                    <option value="Lisans">Lisans</option>
                    <option value="Yüksek Lisans">Yüksek Lisans</option>
                    <option value="Doktora">Doktora</option>
                  </select>
                </div>
                <div className="profile__field">
                  <label className="profile__label">Askerlik Durumu</label>
                  <select className="profile__input" value={form.militaryStatus} onChange={(e) => updateField('militaryStatus', e.target.value)}>
                    <option value="">Seçiniz</option>
                    <option value="Tamamlandı">Tamamlandı</option>
                    <option value="Muaf">Muaf</option>
                    <option value="Tecilli">Tecilli</option>
                    <option value="Yapılmadı">Yapılmadı</option>
                  </select>
                </div>
                <div className="profile__field">
                  <label className="profile__label">LinkedIn</label>
                  <input className="profile__input" value={form.linkedinUrl} onChange={(e) => updateField('linkedinUrl', e.target.value)} placeholder="https://linkedin.com/in/..." />
                </div>
              </div>
              <div className="profile__field profile__field--full">
                <label className="profile__label">Yetkinlikler</label>
                <input className="profile__input" value={form.skills} onChange={(e) => updateField('skills', e.target.value)} placeholder="React, Servis, Muhasebe... (virgülle ayır)" />
              </div>
              <div className="profile__field profile__field--full">
                <label className="profile__label">Hakkında</label>
                <textarea className="profile__textarea" rows={3} value={form.summary} onChange={(e) => updateField('summary', e.target.value)} placeholder="Kısa bir özet yaz..." />
              </div>
            </div>

            {/* CV Yükle */}
            <div className="profile__section">
              <h2 className="profile__section-title">CV Yükle</h2>
              <p className="profile__section-hint">CV yükleyerek kariyer bilgilerini otomatik doldurabilirsin.</p>

              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.doc,.docx"
                className="profile__file-input"
                id="profile-cv"
                onChange={(e) => { if (e.target.files[0]) setCvFile(e.target.files[0]); }}
              />

              {!cvFile ? (
                <label htmlFor="profile-cv" className="profile__upload-area">
                  <Upload size={24} />
                  <span className="profile__upload-title">CV Yükle</span>
                  <span className="profile__upload-hint">PDF, DOC veya DOCX (maks. 5MB)</span>
                </label>
              ) : (
                <div className="profile__cv-selected">
                  <div className="profile__cv-badge">
                    <FileText size={16} />
                    <span>{cvFile.name}</span>
                    <button onClick={removeCv} className="profile__cv-remove"><X size={14} /></button>
                  </div>
                  <button className="profile__cv-analyze" onClick={handleCvUpload} disabled={cvLoading}>
                    {cvLoading ? <><Loader2 size={16} className="profile__spinner" /> Analiz ediliyor...</> : 'CV ile Doldur'}
                  </button>
                </div>
              )}

              {cvProfile && (
                <p className="profile__cv-success"><CheckCircle size={14} /> CV'den bilgiler forma aktarıldı.</p>
              )}
              {error && <p className="profile__error">{error}</p>}
            </div>

            {/* Kaydet */}
            <button className="profile__save" onClick={handleSave} disabled={saving}>
              {saving ? (
                <><Loader2 size={16} className="profile__spinner" /> Kaydediliyor...</>
              ) : saved ? (
                <><CheckCircle size={16} /> Kaydedildi</>
              ) : (
                <><Save size={16} /> Profili Kaydet</>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
